/**
 * The web player's storage: one IndexedDB database holding every file the player keeps — the
 * downloaded media, the SQLite database and the log — as Blobs keyed by path.
 *
 * Shared by the page and the service worker. The page writes files as it downloads them; the
 * service worker reads them back to answer `/__media__/<sha256>` requests, which is how cached
 * pictures and videos play with no network at all.
 *
 * IndexedDB rather than the newer Origin Private File System because signage displays run old
 * browser engines (LG webOS and Samsung Tizen ship Chromium 53–87), and IndexedDB with Blobs is
 * available on all of them. Chromium keeps large Blobs on disk, not in memory.
 */

const DB_NAME = 'oicas-signage-player';
const DB_VERSION = 1;
const FILES = 'files';

export interface StoredFile {
  path: string;
  blob: Blob;
  mtimeMs: number;
}

let opening: Promise<IDBDatabase> | null = null;

export function openStore(): Promise<IDBDatabase> {
  opening ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(FILES)) db.createObjectStore(FILES, { keyPath: 'path' });
    };
    request.onsuccess = () => {
      const db = request.result;
      // Another tab upgrading the schema must not be blocked by this one.
      db.onversionchange = () => {
        db.close();
        opening = null;
      };
      resolve(db);
    };
    request.onerror = () => {
      opening = null;
      reject(request.error ?? new Error('IndexedDB is unavailable'));
    };
  });
  return opening;
}

function done<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

function finished(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
  });
}

export async function getFile(path: string): Promise<StoredFile | null> {
  const db = await openStore();
  const row = await done(db.transaction(FILES, 'readonly').objectStore(FILES).get(path));
  return (row as StoredFile | undefined) ?? null;
}

export async function putFile(path: string, blob: Blob): Promise<void> {
  const db = await openStore();
  const tx = db.transaction(FILES, 'readwrite');
  tx.objectStore(FILES).put({ path, blob, mtimeMs: Date.now() } satisfies StoredFile);
  await finished(tx);
}

export async function deleteFile(path: string): Promise<void> {
  const db = await openStore();
  const tx = db.transaction(FILES, 'readwrite');
  tx.objectStore(FILES).delete(path);
  await finished(tx);
}

/** Moves a file in one transaction, so a reader sees either the old name or the new one. */
export async function moveFile(from: string, to: string): Promise<void> {
  const db = await openStore();
  const tx = db.transaction(FILES, 'readwrite');
  const store = tx.objectStore(FILES);
  const request = store.get(from);
  request.onsuccess = () => {
    const row = request.result as StoredFile | undefined;
    if (!row) {
      tx.abort();
      return;
    }
    store.put({ ...row, path: to, mtimeMs: Date.now() });
    store.delete(from);
  };
  await finished(tx).catch((err) => {
    throw new Error(`Cannot move ${from}: ${(err as Error)?.message ?? 'not found'}`);
  });
}

/** Every stored path that starts with `prefix`. */
export async function listPaths(prefix: string): Promise<string[]> {
  const db = await openStore();
  const range = IDBKeyRange.bound(prefix, `${prefix}￿`);
  const keys = await done(db.transaction(FILES, 'readonly').objectStore(FILES).getAllKeys(range));
  return keys.map(String);
}
